'use client';

import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/solid';
import { useTranslations } from 'next-intl';
import React, { useRef, useState, useCallback } from 'react';
import {
  Navigation,
  Pagination,
  Autoplay,
  A11y,
  Keyboard,
  EffectCoverflow,
  EffectFade,
} from 'swiper/modules';
import { Swiper, SwiperSlide, SwiperRef } from 'swiper/react';
import 'swiper/css';

import { Button, HeroIcon } from '@/components/atoms';
import { CarouselArtCard } from '@/components/molecules';
import { useRouter } from '@/i18n/routing';

import type { CarouselArtProps } from '@/components/organisms/CarouselArt/types';

export const CarouselArt: React.FC<CarouselArtProps> = ({
  items,
  loop = true,
  navigation = true,
  pagination = true,
  effect = 'default',
  spaceBetween = 1,
}) => {
  const router = useRouter();
  const t = useTranslations();
  const swiperRef = useRef<SwiperRef>(null);

  const [activeIndex, setActiveIndex] = useState(0);

  const handleSlideChange = useCallback(
    (swiper: { realIndex: React.SetStateAction<number> }) => {
      setActiveIndex(swiper.realIndex);
    },
    [],
  );

  const handleSlideClick = useCallback(
    (idx: number, id?: string) => {
      if (idx === activeIndex) {
        if (!id) return;
        router.push(`/gallery/${id}`);
      } else {
        swiperRef.current?.swiper.slideToLoop(idx, 300);
      }
    },
    [activeIndex, router],
  );

  const handleNext = useCallback(() => {
    const next = (activeIndex + 1) % items.length;
    swiperRef.current?.swiper.slideToLoop(next, 300);
  }, [activeIndex, items.length]);

  const handlePast = useCallback(() => {
    const past = (activeIndex - 1) % items.length;
    swiperRef.current?.swiper.slideToLoop(past, 300);
  }, [activeIndex, items.length]);

  return (
    <div
      className="w-full relative overflow-hidden flex flex-col justify-center items-start"
      role="region"
      aria-roledescription="carousel"
    >
      <Swiper
        ref={swiperRef}
        modules={[
          Navigation,
          Pagination,
          Autoplay,
          A11y,
          Keyboard,
          EffectCoverflow,
          EffectFade,
        ]}
        loop={loop}
        navigation={false}
        pagination={pagination ? { clickable: true } : false}
        keyboard={{ enabled: true }}
        effect={effect !== 'default' ? effect : undefined}
        slidesPerView="auto"
        slidesPerGroup={1}
        spaceBetween={spaceBetween}
        grabCursor
        centeredSlides={false}
        onSlideChange={handleSlideChange}
        initialSlide={0}
        speed={300}
        className="relative h-full"
      >
        {navigation && (
          <div className="absolute top-1/2 left-0 right-0 flex justify-between -translate-y-1/2 z-50 pointer-events-none">
            {activeIndex > 0 ? (
              <Button
                variant="secondary"
                onClick={handlePast}
                aria-label={t('PREVIOUS_SLIDE')}
                className="pointer-events-auto rounded-full shadow-md"
              >
                <HeroIcon icon={ChevronLeftIcon} size="small" />
              </Button>
            ) : (
              <div></div>
            )}
            {items.length > 1 && (
              <Button
                variant="secondary"
                onClick={handleNext}
                aria-label={t('NEXT_SLIDE')}
                className="pointer-events-auto rounded-full shadow-md"
              >
                <HeroIcon icon={ChevronRightIcon} size="small" />
              </Button>
            )}
          </div>
        )}
        {items.map((item, idx) => {
          const isActive = idx === activeIndex;
          return (
            <SwiperSlide key={idx} className={'max-w-[300px] h-full'}>
              <div
                role="button"
                tabIndex={0}
                onClick={() =>
                  handleSlideClick(idx, item.id ? item.id : items[0].id)
                }
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleSlideClick(idx, item.id);
                  }
                }}
                className={`transform transition-all duration-200 h-full flex flex-col items-start justify-center ${
                  isActive ? 'opacity-100' : 'opacity-30'
                }`}
              >
                <CarouselArtCard
                  id={item.id}
                  title={item.title}
                  image={item.art_work.url}
                  isActive={isActive}
                />
              </div>
            </SwiperSlide>
          );
        })}
      </Swiper>
    </div>
  );
};
